'use client'

import ConfirmModal from '@/components/confirm-modal'
import { exportPlanoDeContas } from '@/lib/api/financial/financial-accounting-api'
import {
    ChartOfAccountsItem,
    ChartOfAccountsType,
    CreateChartOfAccountsData,
    UpdateChartOfAccountsData,
    createChartOfAccounts,
    deleteChartOfAccounts,
    getChartOfAccountsChildren,
    getChartOfAccountsTree,
    getNextCode,
    searchChartOfAccounts,
    updateChartOfAccounts,
} from '@/lib/api/financial/financial-chart-of-accounts-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdAccountTree,
    MdAdd,
    MdArrowBack,
    MdChevronRight,
    MdDelete,
    MdEdit,
    MdExpandMore,
    MdSave,
    MdSearch,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const TYPE_LABELS: Record<ChartOfAccountsType, string> = {
    ASSET: 'Ativo',
    LIABILITY: 'Passivo',
    EQUITY: 'Patrimônio',
    INCOME: 'Receita',
    EXPENSE: 'Despesa',
}

const TYPE_BADGE_CLASS: Record<ChartOfAccountsType, string> = {
    ASSET: 'badge-secondary text-white',
    LIABILITY: 'badge-warning text-white',
    EQUITY: 'badge-secondary text-white',
    INCOME: 'badge-success text-white',
    EXPENSE: 'badge-error text-white',
}

interface AccountForm {
    code: string
    title: string
    type: ChartOfAccountsType
    parentId: number | null
    isDre: boolean
    active: boolean
}

const initialForm: AccountForm = {
    code: '',
    title: '',
    type: 'INCOME',
    parentId: null,
    isDre: false,
    active: true,
}

function TreeNode({
    account,
    level,
    expandedIds,
    childrenMap,
    loadingIds,
    onToggleExpand,
    onEdit,
    onDelete,
    onAddChild,
    canUpdate,
    canDelete,
    canCreate,
}: {
    account: ChartOfAccountsItem
    level: number
    expandedIds: Set<number>
    childrenMap: Map<number, ChartOfAccountsItem[]>
    loadingIds: Set<number>
    onToggleExpand: (account: ChartOfAccountsItem) => void
    onEdit: (account: ChartOfAccountsItem) => void
    onDelete: (id: number) => void
    onAddChild: (parent: ChartOfAccountsItem) => void
    canUpdate: boolean
    canDelete: boolean
    canCreate: boolean
}) {
    const childCount = account._count?.children || 0
    const isExpanded = expandedIds.has(account.id)
    const isLoading = loadingIds.has(account.id)
    const children = childrenMap.get(account.id)

    return (
        <div>
            <div
                className={`flex items-center gap-2 py-2 px-3 hover:bg-base-200/50 rounded-lg group transition-colors ${level === 0 ? 'bg-base-200/30' : ''}`}
                style={{ paddingLeft: `${level * 24 + 12}px` }}
            >
                <button
                    className={`btn btn-ghost btn-xs btn-square ${childCount === 0 ? 'invisible' : ''}`}
                    onClick={() => onToggleExpand(account)}
                >
                    {isLoading ? (
                        <span className="loading loading-spinner loading-xs"></span>
                    ) : isExpanded ? (
                        <MdExpandMore size={18} />
                    ) : (
                        <MdChevronRight size={18} />
                    )}
                </button>

                <span className="font-mono text-sm font-semibold text-secondary min-w-[70px]">
                    {account.code}
                </span>

                <span className={`flex-1 text-sm ${level === 0 ? 'font-bold' : ''}`}>
                    {account.title}
                </span>

                <span className={`badge badge-xs ${TYPE_BADGE_CLASS[account.type]}`}>
                    {TYPE_LABELS[account.type]}
                </span>
                {account.isDre && (
                    <span className="badge badge-xs badge-info text-white">DRE</span>
                )}
                {!account.active && (
                    <span className="badge badge-xs badge-ghost">Inativo</span>
                )}
                {childCount > 0 && (
                    <span className="badge badge-xs badge-ghost">{childCount}</span>
                )}

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canCreate && (
                        <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => onAddChild(account)}
                            title="Adicionar subconta"
                        >
                            <MdAdd size={14} />
                        </button>
                    )}
                    {canUpdate && (
                        <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => onEdit(account)}
                            title="Editar"
                        >
                            <MdEdit size={14} />
                        </button>
                    )}
                    {canDelete && childCount === 0 && (
                        <button
                            className="btn btn-ghost btn-xs text-error"
                            onClick={() => onDelete(account.id)}
                            title="Excluir"
                        >
                            <MdDelete size={14} />
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && children && (
                <div>
                    {children.map(child => (
                        <TreeNode
                            key={child.id}
                            account={child}
                            level={level + 1}
                            expandedIds={expandedIds}
                            childrenMap={childrenMap}
                            loadingIds={loadingIds}
                            onToggleExpand={onToggleExpand}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onAddChild={onAddChild}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                            canCreate={canCreate}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

export default function ChartOfAccountsPage() {
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('financial', 'chart_of_accounts')

    const [rootAccounts, setRootAccounts] = useState<ChartOfAccountsItem[]>([])
    const [loading, setLoading] = useState(true)
    const [typeFilter, setTypeFilter] = useState<ChartOfAccountsType | ''>('')

    // Lazy loading
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
    const [childrenMap, setChildrenMap] = useState<Map<number, ChartOfAccountsItem[]>>(new Map())
    const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set())

    // Busca
    const [searchInput, setSearchInput] = useState('')
    const [searchResults, setSearchResults] = useState<ChartOfAccountsItem[] | null>(null)
    const [searching, setSearching] = useState(false)
    const searchTimeout = useRef<NodeJS.Timeout>(null)

    // Modal
    const modalRef = useRef<HTMLDialogElement>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<AccountForm>(initialForm)
    const [loadingCode, setLoadingCode] = useState(false)
    const [saving, setSaving] = useState(false)

    // Delete
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Parent select - usamos rootAccounts + filhos carregados
    const [allLoadedAccounts, setAllLoadedAccounts] = useState<ChartOfAccountsItem[]>([])

    useEffect(() => {
        loadRoots()
    }, [typeFilter])

    useEffect(() => {
        // Recalcular lista plana de contas carregadas para o select de conta pai
        const flat: ChartOfAccountsItem[] = [...rootAccounts]
        for (const [, children] of childrenMap) {
            flat.push(...children)
        }
        setAllLoadedAccounts(flat)
    }, [rootAccounts, childrenMap])

    const loadRoots = async () => {
        setLoading(true)
        setExpandedIds(new Set())
        setChildrenMap(new Map())
        try {
            const params = typeFilter ? { type: typeFilter as ChartOfAccountsType } : undefined
            const res = await getChartOfAccountsTree(params)
            if (res.success && res.result) {
                setRootAccounts(res.result)
            }
        } catch {
            toast.error('Erro ao carregar plano de contas')
        } finally {
            setLoading(false)
        }
    }

    const handleToggleExpand = useCallback(async (account: ChartOfAccountsItem) => {
        const id = account.id

        if (expandedIds.has(id)) {
            setExpandedIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
            return
        }

        // Se já carregou filhos, apenas expandir
        if (childrenMap.has(id)) {
            setExpandedIds(prev => new Set(prev).add(id))
            return
        }

        // Carregar filhos
        setLoadingIds(prev => new Set(prev).add(id))
        try {
            const res = await getChartOfAccountsChildren(id)
            if (res.success && res.result) {
                setChildrenMap(prev => {
                    const next = new Map(prev)
                    next.set(id, res.result!)
                    return next
                })
                setExpandedIds(prev => new Set(prev).add(id))
            }
        } catch {
            toast.error('Erro ao carregar subcontas')
        } finally {
            setLoadingIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }
    }, [expandedIds, childrenMap])

    // Busca com debounce
    useEffect(() => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current)

        if (!searchInput.trim()) {
            setSearchResults(null)
            return
        }

        searchTimeout.current = setTimeout(async () => {
            setSearching(true)
            try {
                const res = await searchChartOfAccounts(
                    searchInput.trim(),
                    typeFilter || undefined,
                )
                if (res.success && res.result) {
                    setSearchResults(res.result)
                }
            } catch { /* ignore */ }
            finally { setSearching(false) }
        }, 300)

        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current)
        }
    }, [searchInput, typeFilter])

    const fetchNextCode = async (parentId?: number | null) => {
        setLoadingCode(true)
        try {
            const res = await getNextCode(parentId ?? undefined)
            if (res.success && res.result) {
                setForm(prev => ({ ...prev, code: res.result!.code }))
            }
        } catch { /* ignore */ }
        finally { setLoadingCode(false) }
    }

    const openCreateModal = () => {
        setEditingId(null)
        setForm(initialForm)
        modalRef.current?.showModal()
        fetchNextCode(null)
    }

    const openCreateChildModal = (parent: ChartOfAccountsItem) => {
        setEditingId(null)
        setForm({ ...initialForm, type: parent.type, parentId: parent.id, code: '' })
        modalRef.current?.showModal()
        fetchNextCode(parent.id)
    }

    const openEditModal = (account: ChartOfAccountsItem) => {
        setEditingId(account.id)
        setForm({
            code: account.code,
            title: account.title,
            type: account.type,
            parentId: account.parentId,
            isDre: account.isDre,
            active: account.active,
        })
        modalRef.current?.showModal()
    }

    const closeModal = () => {
        modalRef.current?.close()
        setEditingId(null)
        setForm(initialForm)
    }

    const handleParentChange = (parentId: number | null) => {
        setForm(prev => ({ ...prev, parentId }))
        if (!editingId) fetchNextCode(parentId)
    }

    const handleSave = async () => {
        if (!form.title.trim()) {
            toast.warning('Informe o título da conta')
            return
        }

        setSaving(true)
        try {
            if (editingId) {
                const data: UpdateChartOfAccountsData = {
                    title: form.title.trim(),
                    type: form.type,
                    parentId: form.parentId,
                    isDre: form.isDre,
                    active: form.active,
                }
                const res = await updateChartOfAccounts(editingId, data)
                if (res.success) {
                    toast.success('Conta atualizada com sucesso')
                    closeModal()
                    loadRoots()
                } else {
                    toast.error(res.error || 'Erro ao atualizar conta')
                }
            } else {
                const data: CreateChartOfAccountsData = {
                    title: form.title.trim(),
                    type: form.type,
                    parentId: form.parentId,
                    isDre: form.isDre,
                    active: form.active,
                }
                const res = await createChartOfAccounts(data)
                if (res.success) {
                    toast.success('Conta criada com sucesso')
                    closeModal()
                    loadRoots()
                } else {
                    toast.error(res.error || 'Erro ao criar conta')
                }
            }
        } catch {
            toast.error('Erro ao salvar conta')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const res = await deleteChartOfAccounts(deleteId)
            if (res.success) {
                toast.success('Conta excluída com sucesso')
                setDeleteId(null)
                loadRoots()
            } else {
                toast.error(res.error || 'Erro ao excluir conta')
            }
        } catch {
            toast.error('Erro ao excluir conta')
        } finally {
            setDeleting(false)
        }
    }

    const totalAccounts = rootAccounts.length

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">Plano de Contas</h1>
                        <p className="text-base-content/60 text-sm mt-1">
                            Gerencie a estrutura hierárquica de contas financeiras
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="dropdown dropdown-end">
                        <label tabIndex={0} className="btn btn-sm btn-outline gap-1">
                            Exportar
                        </label>
                        <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-50 w-44 p-2 shadow-lg border border-base-300">
                            <li><button onClick={() => { exportPlanoDeContas('xlsx').catch(() => {}) }}>Excel (.xlsx)</button></li>
                            <li><button onClick={() => { exportPlanoDeContas('pdf').catch(() => {}) }}>PDF</button></li>
                        </ul>
                    </div>
                    {canCreate && (
                        <button className="btn btn-secondary" onClick={openCreateModal}>
                            <MdAdd size={20} />
                            Nova Conta
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <MdSearch
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 z-10"
                    />
                    <input
                        type="text"
                        className="input input-bordered input-sm w-full pl-9"
                        placeholder="Buscar por código ou título..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                    {searching && (
                        <span className="loading loading-spinner loading-xs absolute right-3 top-1/2 -translate-y-1/2"></span>
                    )}
                </div>

                <select
                    className="select select-bordered select-sm"
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value as ChartOfAccountsType | '')}
                >
                    <option value="">Todos os tipos</option>
                    <option value="ASSET">Ativo</option>
                    <option value="LIABILITY">Passivo</option>
                    <option value="EQUITY">Patrimônio Líquido</option>
                    <option value="INCOME">Receita</option>
                    <option value="EXPENSE">Despesa</option>
                </select>

                {totalAccounts > 0 && (
                    <span className="text-sm text-base-content/50 ml-auto">
                        {totalAccounts} conta{totalAccounts !== 1 ? 's' : ''} raiz
                    </span>
                )}
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center min-h-[300px]">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                </div>
            )}

            {/* Empty */}
            {!loading && rootAccounts.length === 0 && !searchInput && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body items-center text-center py-16">
                        <MdAccountTree size={48} className="text-base-content/30" />
                        <p className="text-base-content/60 mt-2">Nenhuma conta cadastrada</p>
                        {canCreate && (
                            <button className="btn btn-secondary btn-sm mt-4" onClick={openCreateModal}>
                                <MdAdd size={18} />
                                Cadastrar Conta
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Search results */}
            {searchResults && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body p-4">
                        <p className="text-sm text-base-content/60 mb-3">
                            {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''} para "{searchInput}"
                        </p>
                        {searchResults.length === 0 && (
                            <p className="text-center text-base-content/40 py-8">Nenhuma conta encontrada</p>
                        )}
                        {searchResults.map(account => (
                            <div
                                key={account.id}
                                className="flex items-center gap-3 py-2 px-3 hover:bg-base-200/50 rounded-lg group"
                            >
                                <span className="font-mono text-sm font-semibold text-secondary min-w-[80px]">
                                    {account.code}
                                </span>
                                <span className="flex-1 text-sm">{account.title}</span>
                                {account.parent && (
                                    <span className="text-xs text-base-content/40">
                                        Pai: {account.parent.code} - {account.parent.title}
                                    </span>
                                )}
                                <span className={`badge badge-xs ${TYPE_BADGE_CLASS[account.type]}`}>
                                    {TYPE_LABELS[account.type]}
                                </span>
                                {account.isDre && (
                                    <span className="badge badge-xs badge-info text-white">DRE</span>
                                )}
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {canUpdate && (
                                        <button className="btn btn-ghost btn-xs" onClick={() => openEditModal(account)}>
                                            <MdEdit size={14} />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button className="btn btn-ghost btn-xs text-error" onClick={() => setDeleteId(account.id)}>
                                            <MdDelete size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Tree view */}
            {!loading && rootAccounts.length > 0 && !searchResults && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body p-4">
                        {rootAccounts.map(account => (
                            <TreeNode
                                key={account.id}
                                account={account}
                                level={0}
                                expandedIds={expandedIds}
                                childrenMap={childrenMap}
                                loadingIds={loadingIds}
                                onToggleExpand={handleToggleExpand}
                                onEdit={openEditModal}
                                onDelete={setDeleteId}
                                onAddChild={openCreateChildModal}
                                canUpdate={canUpdate}
                                canDelete={canDelete}
                                canCreate={canCreate}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            <dialog ref={modalRef} className="modal">
                <div className="modal-box !max-w-lg">
                    <h3 className="font-bold text-lg mb-4">
                        {editingId ? 'Editar Conta' : 'Nova Conta'}
                    </h3>

                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Código</span>
                                </label>
                                <div className="input input-bordered w-full flex items-center bg-base-200/50 text-base-content/70">
                                    {loadingCode ? (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    ) : (
                                        <span className="font-mono font-semibold">
                                            {form.code || 'Automático'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Tipo</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.type}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            type: e.target.value as ChartOfAccountsType,
                                        }))
                                    }
                                >
                                    <option value="INCOME">Entrada</option>
                                    <option value="EXPENSE">Saída</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Título</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Receitas com Vendas"
                                value={form.title}
                                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Conta Base</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={form.parentId || ''}
                                onChange={e =>
                                    handleParentChange(
                                        e.target.value ? parseInt(e.target.value, 10) : null,
                                    )
                                }
                            >
                                <option value="">Nenhuma (conta raiz)</option>
                                {allLoadedAccounts
                                    .filter(a => a.active && a.id !== editingId)
                                    .map(a => (
                                        <option key={a.id} value={a.id}>
                                            {a.code} - {a.title}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-6">
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary toggle-sm"
                                        checked={form.isDre}
                                        onChange={e =>
                                            setForm(prev => ({ ...prev, isDre: e.target.checked }))
                                        }
                                    />
                                    <span className="label-text">Compõe DRE</span>
                                </label>
                            </div>

                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary toggle-sm"
                                        checked={form.active}
                                        onChange={e =>
                                            setForm(prev => ({ ...prev, active: e.target.checked }))
                                        }
                                    />
                                    <span className="label-text">Ativa</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdSave size={18} />
                            )}
                            {editingId ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeModal}>close</button>
                </form>
            </dialog>

            {/* Delete confirmation */}
            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Excluir Conta"
                message="Tem certeza de que deseja excluir esta conta? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
